import Papa from "papaparse";

import { downloadCsvWithBom } from "~/src/lib/utils";

import {
  maxImageSlotIndexInRecord,
  recordToPopupImportRow,
  validateHeaderFields,
} from "./parsePopupCsvRow";
import {
  POPUP_IMPORT_ACTION_HEADER,
  POPUP_IMPORT_CSV_HEADERS,
} from "./popupImportCsvHeaders";
import { createPopupImportClientRowId } from "./popupImportRowIdentity";
import type { PopupImportRowValues } from "./validationSchema";

export const IMPORT_MAX_POPUPS = 500;

export type ParseCsvToFormValuesOptions = {
  /** When true, every row's `popup.statusRaw` is set to INACTIVE after parse. */
  forceStatusInactive?: boolean;
};

export function downloadImportTemplate(): void {
  downloadCsvWithBom(
    "ポップアップインポートテンプレート",
    [POPUP_IMPORT_ACTION_HEADER, ...POPUP_IMPORT_CSV_HEADERS].join(",")
  );
}

export function parseCsvToFormValues(
  file: File,
  opts?: ParseCsvToFormValuesOptions
): Promise<{
  rows: PopupImportRowValues[];
  errors: string[];
  csvMaxImageSlot: number;
}> {
  return new Promise((resolve) => {
    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => h.replace(/^\uFEFF/, "").trim(),
      complete: (result) => {
        const errors: string[] = [];
        const fields = result.meta.fields?.filter(Boolean) as string[];

        if (!fields || fields.length === 0) {
          resolve({
            rows: [],
            errors: ["CSVファイルが空です"],
            csvMaxImageSlot: 0,
          });
          return;
        }

        const headerErr = validateHeaderFields(fields);
        if (headerErr) {
          resolve({ rows: [], errors: [headerErr], csvMaxImageSlot: 0 });
          return;
        }

        const data = result.data.filter(
          (row) => row && Object.keys(row).length > 0
        );

        if (data.length === 0) {
          resolve({
            rows: [],
            errors: ["データ行がありません"],
            csvMaxImageSlot: 0,
          });
          return;
        }

        if (data.length > IMPORT_MAX_POPUPS) {
          resolve({
            rows: [],
            errors: [
              `インポートできるポップアップ数は${IMPORT_MAX_POPUPS}件までです（現在: ${data.length}件）`,
            ],
            csvMaxImageSlot: 0,
          });
          return;
        }

        let csvMaxImageSlot = 0;
        data.forEach((record) => {
          csvMaxImageSlot = Math.max(
            csvMaxImageSlot,
            maxImageSlotIndexInRecord(record)
          );
        });

        const rows: PopupImportRowValues[] = [];
        data.forEach((record, idx) => {
          try {
            rows.push({
              ...recordToPopupImportRow(record),
              _clientRowId: createPopupImportClientRowId(),
            });
          } catch {
            errors.push(`${idx + 2}行目: アクション列の値が不正です`);
          }
        });

        if (errors.length > 0) {
          resolve({ rows: [], errors, csvMaxImageSlot: 0 });
          return;
        }

        if (opts?.forceStatusInactive) {
          rows.forEach((row) => {
            row.popup.statusRaw = "INACTIVE";
          });
        }

        resolve({ rows, errors: [], csvMaxImageSlot });
      },
      error: (err) => {
        resolve({
          rows: [],
          errors: [err.message || "CSVの読み込みに失敗しました"],
          csvMaxImageSlot: 0,
        });
      },
    });
  });
}

export { POPUP_IMPORT_CSV_HEADERS, POPUP_IMPORT_ACTION_HEADER };
